/**
 * 站内信通知接口
 * @author ritaxxu
 */
import { requestApiV2 } from '@library'
import {
  IInnerMsgItem,
  IInnerMsgUnreadCount,
  IGetInnerMsgListReq,
  IMarkInnerMsgReadReq,
  ISendInnerMsgReq,
  ISaveInnerMsgDraftReq,
  IResendInnerMsgReq,
  IGetSendRecordListReq,
  ISendRecordItem,
  IGetSendRecordDetailReq,
  IGetSendRecordDetailRsp,
  IGetSendRecordExecuteDetailReq,
  IGetSendRecordExecuteDetailRsp,
  IExportSendRecordExecuteDetailReq,
  IResumeInnerMsgReq,
  ICopyInnerMsgReq,
  IGetInnerMsgTemplateDetailReq,
  IGetInnerMsgTemplateDetailRsp,
} from '@types'

/** 获取我的消息列表 */
export function getInnerMsgList (params: IGetInnerMsgListReq) {
  return requestApiV2<{ total: number, list: IInnerMsgItem[] }>(
    '/trpc.tcc.tcc_main.InnerMsg/GetInnerMsgList',
    params,
  )
}

/** 获取未读消息计数（铃铛角标轮询） */
export function getInnerMsgUnreadCount () {
  return requestApiV2<IInnerMsgUnreadCount>(
    '/trpc.tcc.tcc_main.InnerMsg/GetInnerMsgUnreadCount',
  )
}

/** 标记消息已读（按 recipientIds 单条/多条 或 按 level=1 批量清除普通未读） */
export function markInnerMsgRead (params: IMarkInnerMsgReadReq) {
  return requestApiV2<{ affectedCount: number }>(
    '/trpc.tcc.tcc_main.InnerMsg/MarkInnerMsgRead',
    params,
  )
}

/** 发送站内信通知（二期扩展：para 传 templateId 可对已有模板做草稿转发送/恢复发送） */
export function sendInnerMsg (params: ISendInnerMsgReq) {
  return requestApiV2<{ templateId: number }>(
    '/trpc.tcc.tcc_main.InnerMsg/SendInnerMsg',
    params,
  )
}

/**
 * 保存草稿（独立于发送；宽松保存，落库恒为 status=0）
 * templateId=0/不传=新建草稿；非 0=覆盖更新已有草稿，返回草稿 templateId 供下次覆盖用。
 */
export function saveInnerMsgDraft (params: ISaveInnerMsgDraftReq) {
  return requestApiV2<{ templateId: number }>(
    '/trpc.tcc.tcc_main.InnerMsg/SaveInnerMsgDraft',
    params,
  )
}

/**
 * 再次发送（复用原模板配置，仅传 templateId；30 分钟冷却）
 * @deprecated 二期废弃，请改用「拷贝(copyInnerMsg)+草稿转发送」或「恢复发送(sendInnerMsg 传 templateId)」
 */
export function resendInnerMsg (params: IResendInnerMsgReq) {
  return requestApiV2<{}>(
    '/trpc.tcc.tcc_main.InnerMsg/ResendInnerMsg',
    params,
  )
}

/** 获取发送记录列表 */
export function getSendRecordList (params: IGetSendRecordListReq) {
  return requestApiV2<{ total: number, list: ISendRecordItem[] }>(
    '/trpc.tcc.tcc_main.InnerMsg/GetSendRecordList',
    params,
  )
}

/**
 * 获取发送记录详情（按模板列出所有触发实例）
 * @deprecated 详情弹窗已改为「GetInnerMsgTemplateDetail（内容面）+ GetSendRecordExecuteDetail（统计/接收人面）」，本接口前端不再调用
 */
export function getSendRecordDetail (params: IGetSendRecordDetailReq) {
  return requestApiV2<IGetSendRecordDetailRsp>(
    '/trpc.tcc.tcc_main.InnerMsg/GetSendRecordDetail',
    params,
  )
}

/** 获取单次执行的接收人列表（支持传 templateId 下钻，返回人数/已读率汇总） */
export function getSendRecordExecuteDetail (params: IGetSendRecordExecuteDetailReq) {
  return requestApiV2<IGetSendRecordExecuteDetailRsp>(
    '/trpc.tcc.tcc_main.InnerMsg/GetSendRecordExecuteDetail',
    params,
  )
}

/** 导出单次执行接收人列表（异步任务，磐石通过邮件/RTX 卡片下发下载链接） */
export function exportSendRecordExecuteDetail (params: IExportSendRecordExecuteDetailReq) {
  return requestApiV2<{}>(
    '/trpc.tcc.tcc_main.InnerMsg/ExportSendRecordExecuteDetail',
    params,
  )
}

/** 撤销/删除模板（发送记录「取消发送」走此接口；本人放行、他人仅超管） */
export function cancelInnerMsg (params: { templateId: number }) {
  return requestApiV2<{}>(
    '/trpc.tcc.tcc_main.InnerMsg/CancelInnerMsg',
    params,
  )
}

/**
 * 恢复已撤销模板（独立恢复入口）
 * 仅 status=3 已撤销的手动模板可恢复，按模板已存 sendType 分流：单次定时 3→1 / cron 3→5（重算 nextExecuteAt）。
 * 本人放行、他人仅超管；模板正在执行时返 643002，前端提示稍后重试。
 */
export function resumeInnerMsg (params: IResumeInnerMsgReq) {
  return requestApiV2<{}>(
    '/trpc.tcc.tcc_main.InnerMsg/ResumeInnerMsg',
    params,
  )
}

/** 拷贝手动模板为草稿副本（返回新草稿 templateId，可在草稿中补选发送方式后发送） */
export function copyInnerMsg (params: ICopyInnerMsgReq) {
  return requestApiV2<{ templateId: number }>(
    '/trpc.tcc.tcc_main.InnerMsg/CopyInnerMsg',
    params,
  )
}

/** 获取模板详情（草稿转发送时回填弹窗：标题/正文/等级/接收人/附件/发送方式等配置面全字段） */
export function getInnerMsgTemplateDetail (params: IGetInnerMsgTemplateDetailReq) {
  return requestApiV2<IGetInnerMsgTemplateDetailRsp>(
    '/trpc.tcc.tcc_main.InnerMsg/GetInnerMsgTemplateDetail',
    params,
  )
}

/** 读文件为 base64 字符串（去掉 data URL 前缀 data:xxx;base64,） */
function fileToBase64 (file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      const result = reader.result as string
      resolve(result.split(',')[1] || '')
    }
    reader.readAsDataURL(file)
  })
}

/**
 * 上传富文本内联图片
 * @description 复用邮件模块后端接口（接口定义搬到本项目，前端不跨模块 import intl-tcc-web）：
 *              走 CGW 调 /trpc.tcc.tcc_main.Email/UploadInlineImage，图片以 base64 上送，
 *              后端按 source 区分用途并返回可访问 URL，直接写入正文 <img src>。
 *              source=1 表示站内信：落独立目录、URL 有效期半年（source=0 为邮件默认，本处不用）。
 * @param file 图片文件
 * @returns 图片可访问 URL
 */
export async function uploadInlineImage (file: File): Promise<string> {
  const contentBase64 = await fileToBase64(file)
  // 站内信为群发、无单个客户，故不传邮件场景的 businessKey；仅以 source=1 区分用途（联调确认项）
  const [res, err] = await requestApiV2<{ url: string }>(
    '/trpc.tcc.tcc_main.Email/UploadInlineImage',
    {
      fileName: file.name,
      contentBase64,
      source: 1,
    },
  )
  if (err || !res?.url) {
    throw err || new Error('获取图片地址失败')
  }
  return res.url
}
